import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Heading from 'components/atoms/Heading';
import Paragraph from 'components/atoms/Paragraph';
import Loading from 'components/atoms/Loading';
import Modal from 'components/organisms/Modal';
import FileModal from 'components/organisms/FileModal';
import Task from 'components/molecules/Task';
import { fadeInDown } from 'helpers/animations';
import { ReactComponent as Plus } from 'assets/icons/plus.svg';
import { database } from 'helpers/firebase';
import Topbar from 'components/organisms/Topbar/Topbar';
import favicon from 'assets/icons/agenda.ico';
import { ReactComponent as Search } from 'assets/icons/search.svg';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  min-height: 100vh;
  width: 100%;
  padding: 89px 25px 25px;
`;

const ButtonLink = styled.button`
  display: flex;
  align-items: center;
  background-color: ${({ theme }) => theme.blue};
  height: 32px;
  border-radius: 5px;
  padding: 0 10px;
  border: none;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
  text-decoration: none;
  outline: none;

  :hover {
    background-color: ${({ theme }) => theme.blueHover};
    cursor: pointer;
  }
`;

const AddButton = styled.button`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  height: 48px;
  background-color: ${({ theme }) => theme.gray4};
  border: none;
  border-radius: 50px;
  padding: 8px 12px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
  cursor: pointer;
  outline: none;
  transition: background-color 0.05s ease-in-out;

  :hover {
    background-color: ${({ theme }) => theme.gray3};
  }
`;

const StyledPlus = styled(Plus)`
  height: 34px;
  width: 34px;
  fill: ${({ theme }) => theme.textSecondary};
`;

const Center = styled.div`
  margin: auto;
`;

const Secondary = styled.span`
  color: ${({ theme }) => theme.textSecondary};
  font-weight: 400;
`;

const StyledHeading = styled(Heading)`
  font-size: 24pt;
  animation: ${fadeInDown} ${({ theme }) => theme.fadeInDown};
  margin-top: 15px;
  color: ${({ theme, isError }) => (isError ? theme.red : theme.text)} !important;

  ${Secondary} {
    color: ${({ theme, isError }) => (isError ? theme.red : theme.textSecondary)} !important;
  }
`;

const SecondaryHeading = styled(StyledHeading)`
  color: ${({ theme }) => theme.textSecondary};
`;


const Container = styled.div`
  margin-bottom: 50px;
`;

const SearchLabel = styled.label`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  background-color: ${({ theme }) => theme.gray3};
  height: 32px;
  width: 230px;
  border-radius: 5px;
  padding: 0 0 0 5px;
  box-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  transition: width 0.3s ease-in-out;

  :focus-within {
    background-color: ${({ theme }) => theme.gray2};
    width: 300px;
  }

  svg {
    height: 24px;
    width: auto;
    fill: ${({ theme }) => theme.textSecondary};
    margin-right: 5px;
  }
`;

const SearchInput = styled.input`
  border: none;
  height: 100%;
  width: 100%;
  background-color: transparent;
  color: ${({ theme }) => theme.text};
  font-size: ${({ theme }) => theme.body};
  font-family: 'Poppins', sans-serif;
  outline: none;

  ::placeholder {
    color: ${({ theme }) => theme.textSecondary};
    font-size: ${({ theme }) => theme.body};
    font-family: 'Poppins', sans-serif;
  }
`;

const monthsInYear = [
  'stycznia',
  'lutego',
  'marca',
  'kwietnia',
  'maja',
  'czerwca',
  'lipca',
  'sierpnia',
  'września',
  'października',
  'listopada',
  'grudnia',
];

const daysInWeek = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];

const getDateFromTimestamp = (ts) => {
  const date = new Date(parseInt(ts, 10));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayTs = today.getTime();

  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowTs = tomorrow.getTime();

  const day = date.getDate();
  const month = date.getMonth();
  const year = date.getFullYear();

  const currentYear = new Date().getFullYear();

  let isYear = false;
  if (year !== currentYear) {
    isYear = true;
  }

  if (todayTs === parseInt(ts, 10)) {
    return 'Dzisiaj';
  }
  if (tomorrowTs === parseInt(ts, 10)) {
    return 'Jutro';
  }
  return `${day} ${monthsInYear[month]}${isYear ? ` ${year}` : ''}`;
};

const getDayNameFromTimestamp = (ts) => {
  const date = new Date(parseInt(ts, 10));

  return daysInWeek[date.getDay()];
};

function Agenda({ isAdmin, isVerified, files }) {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isFileModalVisible, setIsFileModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [noDate, setNoDate] = useState([]);
  const [archivised, setArchivised] = useState([]);
  const [tasksUnordered, setTasksUnordered] = useState([]);
  const [checked, setChecked] = useState([]);
  const [isUpdate, setIsUpdate] = useState(0);
  const [editId, setEditId] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [isArchive, setIsArchive] = useState(false);
  const [query, setQuery] = useState('');

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleWindowSizeChange = () => {
      setIsMobile(window.innerWidth <= 700);
    };
    handleWindowSizeChange();
    window.addEventListener('resize', handleWindowSizeChange);
    return () => {
      window.removeEventListener('resize', handleWindowSizeChange);
    };
  }, []);

  useEffect(() => {
    const arr = [];

    const ref = database.ref('/tasks');

    ref.once('value').then((snapshot) => {
      snapshot.forEach((childSnapshot) => {
        arr.push({ ...childSnapshot.val(), key: childSnapshot.key });
      });

      arr.forEach((task, i) => {
        if (task.files) {
          const filesArr = [];

          Object.keys(task.files).forEach((key) => {
            const found = files.find((file) => file.key === task.files[key]);
            if (found) {
              filesArr.push(found);
            }
          });

          arr[i].files = filesArr;
        }
      });

      setTasksUnordered(arr);
      setIsLoading(false);
    });
  }, [files, isUpdate]);

  useEffect(() => {
    const ref = database.ref('/tasks');

    ref.on('child_added', () => {
      setIsUpdate((prevState) => prevState + 1);
    });

    ref.on('child_changed', () => {
      setIsUpdate((prevState) => prevState + 1);
    });

    ref.on('child_removed', () => {
      setIsUpdate((prevState) => prevState + 1);
    });
  }, []);

  useEffect(() => {
    const obj = {};
    const archivisedObj = {};
    const noDateArr = [];
    const checkedArr = [];

    const sorted = tasksUnordered
      .filter((obj) => obj.name.toLowerCase().includes(query.toLowerCase()))
      .sort((a, b) => a.ts - b.ts);

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    sorted.forEach((task) => {
      if (task.ts < now.getTime() && task.isChecked) {
        if (archivisedObj[task.ts]) {
          archivisedObj[task.ts].push(task);
        } else {
          archivisedObj[task.ts] = [task];
        }
      } else if (task.isChecked) {
        checkedArr.push(task);
      } else if (task.isNoDate) {
        noDateArr.push(task);
      } else if (obj[task.ts]) {
        obj[task.ts].push(task);
      } else {
        obj[task.ts] = [task];
      }
    });

    setTasks(obj);
    setArchivised(archivisedObj);
    setNoDate(noDateArr);
    setChecked(checkedArr);
  }, [tasksUnordered, query]);

  const handleEdit = (id) => {
    setEditId(id);
    setIsModalVisible(true);
  };

  const hideModal = () => {
    setIsModalVisible(false);
    setEditId(null);
  };

  const handleOpenFileModal = (id) => {
    setIsFileModalVisible(true);
    setFileId(id);
  };

  return (
    <Wrapper>
      {isModalVisible ? <Modal hideModal={hideModal} id={editId} /> : null}
      {isFileModalVisible ? (
        <FileModal setIsFileModalVisible={setIsFileModalVisible} files={files} id={fileId} />
      ) : null}
      <Topbar
        title="Zadania"
        favicon={favicon}
        additionalContent={
          <>
            <ButtonLink type="button" onClick={() => setIsArchive((prevState) => !prevState)}>
              <Paragraph>{isArchive ? 'Zadania' : 'Archiwum'}</Paragraph>
            </ButtonLink>
            {isVerified || isAdmin ? (
              <AddButton type="button" onClick={() => setIsModalVisible(true)}>
                <StyledPlus />
                <Paragraph>Dodaj zadanie</Paragraph>
              </AddButton>
            ) : null}
            <SearchLabel>
              <Search />
              <SearchInput
                type="text"
                placeholder="Szukaj"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </SearchLabel>
          </>
        }
      />
      {/* eslint-disable-next-line no-nested-ternary */}
      {isLoading ? (
        <Center>
          <Loading />
        </Center>
      ) : // eslint-disable-next-line no-nested-ternary
      isArchive ? (
        Object.keys(archivised).length ? (
          Object.keys(archivised)
            .reverse()
            .map((key) => (
              <Container key={key}>
                <StyledHeading>
                  {getDateFromTimestamp(key)}, <Secondary>{getDayNameFromTimestamp(key)}</Secondary>
                </StyledHeading>
                {archivised[key].map(
                  ({ name, key: k, description, files: f, person, type, isChecked }, i) => (
                    <Task
                      key={k}
                      id={k}
                      name={name}
                      description={description}
                      files={f}
                      person={person}
                      type={type}
                      isChecked={isChecked}
                      delay={i}
                      isAdmin={isAdmin}
                      handleEdit={handleEdit}
                      handleOpenFileModal={handleOpenFileModal}
                      isVerified={isVerified}
                      isMobile={isMobile}
                    />
                  ),
                )}
              </Container>
            ))
        ) : (
          <Center>
            <SecondaryHeading>Pusto</SecondaryHeading>
          </Center>
        )
      ) : Object.keys(tasks).length || noDate.length || checked.length ? (
        <Container>
          {noDate.length ? (
            <>
              <StyledHeading>
                Nieokreślone <Secondary>({noDate.length})</Secondary>
              </StyledHeading>
              {noDate.map(({ name, key: k, description, files: f, person, type, isChecked }, i) => (
                <Task
                  key={k}
                  id={k}
                  name={name}
                  description={description}
                  files={f}
                  person={person}
                  type={type}
                  isChecked={isChecked}
                  delay={i}
                  isAdmin={isAdmin}
                  handleEdit={handleEdit}
                  handleOpenFileModal={handleOpenFileModal}
                  isVerified={isVerified}
                  isMobile={isMobile}
                />
              ))}
            </>
          ) : null}
          {Object.keys(tasks).map((key) => (
            <React.Fragment key={key}>
              <StyledHeading isError={key < Date.now()}>
                {getDateFromTimestamp(key)},{' '}
                <Secondary>
                  {getDayNameFromTimestamp(key)} ({tasks[key].length})
                </Secondary>
              </StyledHeading>
              {tasks[key].map(
                ({ name, key: k, description, files: f, person, type, isChecked }, i) => (
                  <Task
                    key={k}
                    id={k}
                    name={name}
                    description={description}
                    files={f}
                    person={person}
                    type={type}
                    isChecked={isChecked}
                    delay={i}
                    isAdmin={isAdmin}
                    handleEdit={handleEdit}
                    handleOpenFileModal={handleOpenFileModal}
                    isVerified={isVerified}
                    isMobile={isMobile}
                  />
                ),
              )}
            </React.Fragment>
          ))}
          {checked.length ? (
            <>
              <StyledHeading>
                Zaliczone <Secondary>({checked.length})</Secondary>
              </StyledHeading>
              {checked.map(
                ({ name, key: k, description, files: f, person, type, isChecked }, i) => (
                  <Task
                    key={k}
                    id={k}
                    name={name}
                    description={description}
                    files={f}
                    person={person}
                    type={type}
                    isChecked={isChecked}
                    delay={i}
                    isAdmin={isAdmin}
                    handleEdit={handleEdit}
                    handleOpenFileModal={handleOpenFileModal}
                    isVerified={isVerified}
                    isMobile={isMobile}
                  />
                ),
              )}
            </>
          ) : null}
        </Container>
      ) : (
        <Center>
          <SecondaryHeading>
            {query.length ? (
              <>
                Nie ma takich zadań{' '}
                <span role="img" aria-label="emoji">
                  🕵🏻‍♂️
                </span>
              </>
            ) : (
              <>
                Żadnych zadań{' '}
                <span role="img" aria-label="emoji">
                  😎
                </span>
              </>
            )}
          </SecondaryHeading>
        </Center>
      )}
    </Wrapper>
  );
}

Agenda.propTypes = {
  isAdmin: PropTypes.bool,
  isVerified: PropTypes.bool,
  files: PropTypes.arrayOf(
    PropTypes.shape({
      folder: PropTypes.string,
      key: PropTypes.string,
      lastModified: PropTypes.number,
      uploaded: PropTypes.number,
      name: PropTypes.string,
      size: PropTypes.number,
      type: PropTypes.string,
    }),
  ),
};

Agenda.defaultProps = {
  isAdmin: false,
  isVerified: false,
  files: [],
};

export default Agenda;
